import React from 'react';
import {
  Badge, Button, EmptyState, IconButton, SkeletonRows, initialsOf,
} from '../../../components/app2';

/**
 * Listado de Super Admins (rediseño jul-2026).
 *
 * Mismas columnas y las mismas dos acciones por fila (restablecer contraseña y
 * eliminar), con el molde de tabla del sistema y tarjetas en móvil.
 *
 * Las protecciones sobre el propio usuario se mantienen y ahora se EXPLICAN:
 * antes eran una casilla y dos botones en gris con un globo de ayuda que solo
 * aparecía al pasar el cursor (invisible en táctil). Ahora la fila propia lleva
 * la etiqueta "Tú" y, en lugar de acciones apagadas, dice "Tu cuenta" con el
 * motivo en el título.
 */

const GRID = '38px minmax(170px,1.3fr) minmax(190px,1.3fr) 118px 150px 84px';
const GRID_GAP = 14;
const GRID_MIN = 860;

const formatLastLogin = (lastLogin) => {
  if (!lastLogin) return 'Nunca';
  return new Date(lastLogin)?.toLocaleDateString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatCreated = (createdAt) => {
  if (!createdAt) return '—';
  return new Date(createdAt)?.toLocaleDateString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const MOTIVO_PROPIO = 'Por seguridad no puedes eliminar ni restablecer la contraseña de tu propio usuario.';

const SystemUsersList = ({
  users,
  selectedUsers,
  currentUserId,
  isAllSelected,
  onSelectAll,
  onSelectUser,
  onDeleteUser,
  onResetPassword,
  loading,
  emptyStateProps,
}) => {
  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        <SkeletonRows rows={5} title={false} />
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <EmptyState
        icon={emptyStateProps?.hasFilters ? 'person_search' : 'shield_person'}
        title={emptyStateProps?.hasFilters
          ? 'Sin coincidencias'
          : 'No hay Super Admins en el sistema'}
        text={emptyStateProps?.hasFilters
          ? 'Ajusta la búsqueda o el filtro de estado.'
          : 'Crea el primero para que alguien pueda administrar la plataforma.'}
        action={emptyStateProps?.hasFilters
          ? null
          : (
            <Button icon="person_add" onClick={emptyStateProps?.onCreateNew}>
              Crear el primer Super Admin
            </Button>
          )}
        className="a2-card"
        style={{ border: 'none' }}
      />
    );
  }

  const esActual = (userId) => userId === currentUserId;

  const Persona = ({ user }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
      <span
        className="a2-avatar"
        style={{
          width: 38, height: 38, fontSize: '.76rem', flexShrink: 0,
          background: 'var(--surface-2)', border: '1px solid var(--line-2)', color: 'var(--text)',
        }}
      >
        {initialsOf(`${user?.first_name || ''} ${user?.last_name || ''}`)}
      </span>
      <div style={{ minWidth: 0 }}>
        {/* La etiqueta "Tú" va FUERA del truncado: si el nombre es largo debe
            recortarse el nombre, nunca la marca de que es tu propia cuenta. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            className="a2-tcell-strong a2-truncate"
            title={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
          >
            {user?.first_name} {user?.last_name}
          </span>
          {esActual(user?.id) ? (
            <Badge tone="brand" icon="person" style={{ flexShrink: 0 }}>Tú</Badge>
          ) : null}
        </div>
        <div className="a2-tcell-muted a2-mono a2-truncate">
          {user?.documentId || user?.id}
        </div>
      </div>
    </div>
  );

  const Correo = ({ user }) => (
    <div style={{ minWidth: 0 }}>
      <div className="a2-tcell a2-truncate" title={user?.email}>{user?.email}</div>
      {user?.must_change_password ? (
        <Badge tone="warn" icon="key_off" style={{ marginTop: 4 }}>Debe cambiar contraseña</Badge>
      ) : null}
    </div>
  );

  const Estado = ({ user }) => (
    <Badge
      tone={user?.is_active ? 'ok' : 'neutral'}
      icon={user?.is_active ? 'check_circle' : 'cancel'}
    >
      {user?.is_active ? 'Activo' : 'Inactivo'}
    </Badge>
  );

  const Acciones = ({ user }) => {
    if (esActual(user?.id)) {
      return (
        <span className="a2-tcell-muted" title={MOTIVO_PROPIO} style={{ whiteSpace: 'nowrap' }}>
          Tu cuenta
        </span>
      );
    }
    const nombre = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email;
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <IconButton
          icon="lock_reset"
          title={`Restablecer contraseña de ${nombre}`}
          aria-label={`Restablecer contraseña de ${nombre}`}
          onClick={() => onResetPassword?.(user?.id)}
        />
        <IconButton
          icon="delete"
          tone="danger"
          title={`Eliminar a ${nombre}`}
          aria-label={`Eliminar a ${nombre}`}
          onClick={() => onDeleteUser?.(user?.id)}
        />
      </div>
    );
  };

  const Casilla = ({ user }) => {
    const propio = esActual(user?.id);
    return (
      <label
        className="a2-check"
        title={propio ? MOTIVO_PROPIO : `Seleccionar a ${user?.first_name} ${user?.last_name}`}
      >
        <input
          type="checkbox"
          checked={selectedUsers?.includes(user?.id) || false}
          onChange={() => onSelectUser?.(user?.id)}
          disabled={propio}
          aria-label={`Seleccionar a ${user?.first_name} ${user?.last_name}`}
        />
      </label>
    );
  };

  return (
    <>
      {/* Escritorio */}
      <div className="a2-only-lg">
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: GRID_MIN }}>
            <div className="a2-thead" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
              <label className="a2-check" title="Seleccionar todos (excepto tu propio usuario)">
                <input
                  type="checkbox"
                  checked={Boolean(isAllSelected)}
                  onChange={onSelectAll}
                  aria-label="Seleccionar todos los Super Admins"
                />
              </label>
              <div>Usuario</div>
              <div>Correo</div>
              <div>Estado</div>
              <div>Último acceso</div>
              <div style={{ textAlign: 'right' }}>Acciones</div>
            </div>

            {users.map((user) => (
              <div
                key={user?.id}
                className="a2-trow"
                style={{
                  gridTemplateColumns: GRID,
                  gap: GRID_GAP,
                  background: selectedUsers?.includes(user?.id) ? 'var(--brand-soft)' : undefined,
                }}
              >
                <Casilla user={user} />
                <Persona user={user} />
                <Correo user={user} />
                <div><Estado user={user} /></div>
                <div className="a2-tcell">{formatLastLogin(user?.last_login)}</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Acciones user={user} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Móvil */}
      <div className="a2-only-sm">
        {users.map((user) => (
          <div
            key={user?.id}
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--line)',
              background: selectedUsers?.includes(user?.id) ? 'var(--brand-soft)' : undefined,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ paddingTop: 10 }}><Casilla user={user} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Persona user={user} />
                <div style={{ marginTop: 8 }}><Correo user={user} /></div>
              </div>
              <Estado user={user} />
            </div>

            <div
              style={{
                marginTop: 12, display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10,
              }}
            >
              <div>
                <div className="a2-tcell-muted">Último acceso</div>
                <div className="a2-tcell">{formatLastLogin(user?.last_login)}</div>
              </div>
              <div>
                <div className="a2-tcell-muted">Creado</div>
                <div className="a2-tcell">{formatCreated(user?.createdAt)}</div>
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <Acciones user={user} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default SystemUsersList;
